/*
  Add-on choice gate (brief task B02).

  Why this exists
  ---------------
  An add-on card — "+ Add" in the drawer, a complementary card on a PDP, an upsell in the full cart —
  used to post `product.first_available_variant.id` whenever the merchant had the theme's own variant
  picker switched off. For the Essentials Tee that first available variant is size S, so a single
  unpremeditated click created a size the shopper never chose. That is defect OBS01, reproduced in
  evidence/aov/cart-truth.json: one click, `Size : S`, £20.00, no selection step.

  What it does, per card marked `data-runit-addon-choice` (multi-variant add-ons only)
  -----------------------------------------------------------------------------------
  * Keeps every control in the card disabled while no real combination has been chosen.
  * Resolves a combination from either the theme's own picker or the required-choice selector the
    theme now renders, and counts an option group only after the shopper has actually interacted with
    it — a browser's default first value is not a choice, it is the browser filling a gap.
  * Writes the resolved variant id into the form's hidden input *and* the card's `data-id`, so
    whichever path submits (the button's form, or the theme's toggle handler) carries the choice.
  * Revalidates the choice against `/products/<handle>.js` before the add, so a variant that sold out
    after the card was rendered cannot be bought on stale markup.
  * Shows the chosen variant's own price when the card prices per variant (and when the merchant has
    not layered a block-level discount on top — that setting wins, because it is deliberate).
  * Remembers the choice for the session, so a cart re-render that replaces the card's markup does not
    silently discard the size the shopper just picked.
  * Explains itself in a live region and sends focus to the selector when someone tries to add without
    choosing, so the gate is usable with a screen reader and without a mouse.

  Nothing here prices, discounts, or mutates the cart: the theme's own product form still performs the
  add, with the id this script resolved.
*/
(function () {
  'use strict';

  var CARD = '[data-runit-addon-choice]';
  var READY = 'data-runit-addon-ready';
  var RESOLVED = 'data-runit-addon-resolved';
  var STORE_PREFIX = 'rl-addon-choice:';

  function jsonFrom(value) {
    try {
      return value ? JSON.parse(value) : null;
    } catch (e) {
      return null;
    }
  }

  function remember(handle, values) {
    try {
      sessionStorage.setItem(STORE_PREFIX + handle, JSON.stringify(values));
    } catch (e) {
      /* private mode, quota, disabled storage — the gate still works, it just forgets */
    }
  }

  function recall(handle) {
    try {
      return jsonFrom(sessionStorage.getItem(STORE_PREFIX + handle));
    } catch (e) {
      return null;
    }
  }

  function money(amount, currency) {
    try {
      return new Intl.NumberFormat(navigator.language || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 2
      }).format(amount / 100);
    } catch (e) {
      return null;
    }
  }

  function status(card, message) {
    var el = card.querySelector('[data-runit-addon-status]');
    if (el && message) el.textContent = message;
  }

  function selectsOf(card) {
    var own = card.querySelectorAll('select[data-runit-addon-option]');
    if (own.length) return Array.prototype.slice.call(own);
    return Array.prototype.slice.call(card.querySelectorAll('.upsell__variant-picker select'));
  }

  function controlsOf(card) {
    return Array.prototype.slice.call(card.querySelectorAll('[data-runit-addon-control]'));
  }

  function optionValue(variant, index) {
    return String(variant['option' + index] || '').trim().toLowerCase();
  }

  function initCard(card) {
    if (card.getAttribute(READY) === 'true') return;
    var variants = jsonFrom(card.getAttribute('data-runit-addon-variants'));
    if (!variants || !variants.length) return;
    card.setAttribute(READY, 'true');

    var handle = card.getAttribute('data-runit-addon-handle') || '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    var selects = selectsOf(card);
    var controls = controlsOf(card);
    var input = card.querySelector('[data-runit-addon-input]') || card.querySelector('input[name="id"]');
    var chosen = null;
    var touched = {};

    function store() {
      var values = {};
      selects.forEach(function (select, index) {
        values['o' + (index + 1)] = select.value;
      });
      if (handle) remember(handle, values);
    }

    function resolve() {
      var complete = selects.length > 0;
      for (var i = 0; i < selects.length; i += 1) {
        if (!touched[i + 1] || !selects[i].value) complete = false;
      }
      if (!complete) return null;
      for (var v = 0; v < variants.length; v += 1) {
        var match = true;
        for (var s = 0; s < selects.length; s += 1) {
          if (optionValue(variants[v], s + 1) !== String(selects[s].value).trim().toLowerCase()) {
            match = false;
            break;
          }
        }
        if (match) return variants[v];
      }
      return null;
    }

    function apply(next) {
      chosen = next;
      if (next) {
        card.setAttribute(RESOLVED, 'true');
        card.setAttribute('data-id', next.id);
        if (input) input.value = next.id;
        controls.forEach(function (control) {
          control.disabled = false;
          control.removeAttribute('aria-disabled');
        });
        status(card, next.title || '');
        showPrice(next);
      } else {
        card.removeAttribute(RESOLVED);
        card.setAttribute('data-id', '');
        if (input) input.value = '';
        controls.forEach(function (control) {
          control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
        });
      }
    }

    function showPrice(variant) {
      if (card.getAttribute('data-update-prices') === 'true') return;
      var regular = card.querySelector('.upsell__price .regular-price');
      if (!regular) return;
      var text = money(variant.price, currency);
      if (text) regular.textContent = text;
      var compare = card.querySelector('.upsell__price .compare-price');
      if (compare) {
        var compareAt = variant.compare_at_price && variant.compare_at_price > variant.price
          ? money(variant.compare_at_price, currency)
          : null;
        if (compareAt) {
          compare.textContent = compareAt;
          compare.classList.remove('hidden');
        } else {
          compare.classList.add('hidden');
        }
      }
    }

    // Revalidate against the live product before the add: the card may be minutes old and the
    // variant may have sold out since it rendered.
    function revalidate(variant) {
      if (!handle || !window.fetch) return;
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') +
        'products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (product) {
          if (!product || !product.variants) return;
          var live = null;
          for (var i = 0; i < product.variants.length; i += 1) {
            if (String(product.variants[i].id) === String(variant.id)) live = product.variants[i];
          }
          if (!live) return;
          if (live.available === false) {
            card.removeAttribute(RESOLVED);
            controls.forEach(function (control) {
              control.disabled = true;
              control.setAttribute('aria-disabled', 'true');
            });
            status(card, 'That option just sold out — choose another.');
          }
        })
        .catch(function () { /* never block the add on a failed lookup */ });
    }

    function update() {
      var next = resolve();
      if (next && (!chosen || String(chosen.id) !== String(next.id))) {
        apply(next);
        store();
        revalidate(next);
        return;
      }
      if (!next && chosen) {
        apply(null);
        store();
      }
    }

    selects.forEach(function (select, index) {
      select.setAttribute('data-runit-addon-bound', 'true');
      select.addEventListener('change', function () {
        touched[index + 1] = true;
        update();
      });
    });

    // Restore a choice made before the card was re-rendered, so a cart update cannot quietly reset
    // the shopper's size to nothing (and leave them staring at a dead button).
    var remembered = handle ? recall(handle) : null;
    if (remembered) {
      selects.forEach(function (select, index) {
        var value = remembered['o' + (index + 1)];
        if (!value) return;
        var found = false;
        for (var i = 0; i < select.options.length; i += 1) {
          if (select.options[i].value === value) found = true;
        }
        if (!found) return;
        select.value = value;
        touched[index + 1] = true;
      });
    }

    // The control starts disabled in Liquid, but the theme's own availability code also toggles that
    // attribute; while nothing is chosen, this keeps it honest.
    if (window.MutationObserver) {
      var observer = new MutationObserver(function () {
        if (card.getAttribute(RESOLVED) === 'true') return;
        controls.forEach(function (control) {
          if (!control.disabled) control.disabled = true;
        });
      });
      controls.forEach(function (control) {
        observer.observe(control, { attributes: true, attributeFilter: ['disabled'] });
      });
    }

    apply(resolve());
    if (!card.hasAttribute(RESOLVED)) {
      var first = selects[0];
      if (first) {
        controls.forEach(function (control) {
          control.addEventListener('click', function (event) {
            if (card.getAttribute(RESOLVED) === 'true') return;
            event.preventDefault();
            event.stopPropagation();
            first.focus();
          });
        });
        var form = first.closest('form');
        var owner = form || (input && input.form);
        if (owner) {
          owner.addEventListener(
            'submit',
            function (event) {
              if (card.getAttribute(RESOLVED) === 'true') return;
              event.preventDefault();
              event.stopPropagation();
              status(card, 'Choose ' + (first.getAttribute('data-runit-addon-option-name') || 'an option').toLowerCase() + ' to add this item.');
              first.focus();
            },
            true
          );
        }
      }
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i += 1) initCard(cards[i]);
  }

  function refresh() {
    init(document);
  }

  function boot() {
    refresh();
    if (window.MutationObserver && document.body) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          refresh();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
